import { Button } from "@app/components/ui/button";
import { Input } from "@app/components/ui/input";
import { Trans, useLingui } from "@lingui/macro";
import { Search, X } from "lucide-react";
import { useState } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import API from "@app/services/api";
import dayjs from "dayjs";
import { StethoscopeLoader } from "@app/components/StethoscopeLoader";

// Le backend renvoie du texte enrichi en markdown (gras, listes) généré par le LLM.
// Tailwind Preflight neutralise le style par défaut des balises (listes sans puces,
// gras non appliqué) : on mappe donc explicitement chaque élément sur la palette du projet.
const markdownComponents: Components = {
  p: ({ children }) => <p className="text-sm text-slate-900 mb-2 last:mb-0">{children}</p>,
  strong: ({ children }) => <strong className="font-semibold text-slate-900">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  ul: ({ children }) => (
    <ul className="list-disc pl-5 my-2 space-y-1 text-sm text-slate-900">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="list-decimal pl-5 my-2 space-y-1 text-sm text-slate-900">{children}</ol>
  ),
  li: ({ children }) => <li className="text-sm text-slate-900">{children}</li>,
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-primary underline underline-offset-2"
    >
      {children}
    </a>
  ),
  code: ({ children }) => (
    <code className="px-1 py-0.5 rounded bg-blue-100 text-sm font-mono">{children}</code>
  ),
  h1: ({ children }) => <p className="text-sm font-semibold text-slate-900 mb-1">{children}</p>,
  h2: ({ children }) => <p className="text-sm font-semibold text-slate-900 mb-1">{children}</p>,
  h3: ({ children }) => <p className="text-sm font-semibold text-slate-900 mb-1">{children}</p>,
};

interface Citation {
  type: "observation" | "document" | "field";
  id: string;
  label: string;
  source_doc_id?: string;
  source_excerpt?: string;
  clinical_date?: string;
}

interface AskResponse {
  text: string;
  citations: Citation[];
  matched_intent?: string;
}

interface PatientSearchBarProps {
  tenantId: string;
  patientId: string;
  // Time-slider date (ISO ``YYYY-MM-DD``) or null for the present. When set, the answer is
  // restricted to contribution-log values dated on/before it — consistent with what the page
  // shows at that slider position.
  at?: string | null;
}

export function PatientSearchBar({ tenantId, patientId, at }: PatientSearchBarProps) {
  const { t } = useLingui();
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<AskResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    const trimmed = query.trim();
    if (!trimmed || loading) return;
    setLoading(true);
    setError(null);
    try {
      // The endpoint returns the AskResponse flat ({text, citations, ...}); a transport
      // error returns {ok:false, error}. Distinguish on the presence of ``text``.
      const res = await API.post({
        path: `/patient/${tenantId}/${patientId}/ask`,
        body: at ? { query: trimmed, at } : { query: trimmed },
      });
      if (res && typeof (res as { text?: unknown }).text === "string") {
        setResponse(res as AskResponse);
      } else {
        setError((res as { error?: string })?.error || t`Erreur lors de la requête`);
        setResponse(null);
      }
    } catch {
      setError(t`Erreur lors de la requête`);
      setResponse(null);
    } finally {
      setLoading(false);
    }
  }

  function clear() {
    setResponse(null);
    setError(null);
    setQuery("");
  }

  return (
    <div
      data-tour="patient-ask"
      className="px-6 py-3 bg-white border-b border-slate-200"
    >
      <div className="flex gap-2 items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") submit();
            }}
            placeholder={t`Poser une question sur ce dossier...`}
            disabled={loading}
            className="pl-9 h-9 border-slate-200 focus-visible:ring-primary"
          />
        </div>
        <Button
          onClick={submit}
          disabled={loading || !query.trim()}
          size="sm"
          className="bg-primary text-primary-foreground hover:bg-primary/90 h-9"
        >
          {loading ? (
            <span className="flex items-center gap-1.5">
              <StethoscopeLoader size={16} />
              <Trans>Recherche...</Trans>
            </span>
          ) : (
            <Trans>Demander</Trans>
          )}
        </Button>
        {(response || error) && (
          <Button
            onClick={clear}
            size="sm"
            variant="outline"
            className="border-slate-200 hover:border-primary/50 h-9"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {loading && !error && (
        <div className="mt-3 px-4 py-4 rounded border border-primary/20 bg-blue-50 flex items-center gap-3">
          <StethoscopeLoader size={40} />
          <span className="text-sm text-slate-600">
            <Trans>Analyse du dossier en cours...</Trans>
          </span>
        </div>
      )}

      {error && (
        <div className="mt-3 px-3 py-2 rounded border border-red-200 bg-red-50 text-sm text-red-900">
          {error}
        </div>
      )}

      {response && !error && !loading && (
        <div className="mt-3 px-4 py-3 rounded border border-primary/20 bg-blue-50">
          <div className="text-sm text-slate-900">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {response.text}
            </ReactMarkdown>
          </div>
          {response.citations.length > 0 && (
            <div className="mt-3 pt-3 border-t border-blue-100">
              <p className="text-2xs font-medium uppercase tracking-wide text-slate-500 mb-2">
                <Trans>Sources</Trans>
              </p>
              <ul className="space-y-1.5">
                {response.citations.map((c) => (
                  <li
                    key={`${c.type}-${c.id}`}
                    className="text-sm text-slate-700 flex items-start gap-2"
                  >
                    <span className="text-3xs uppercase tracking-wide text-primary mt-1 shrink-0 min-w-[64px]">
                      {c.type}
                    </span>
                    <span className="flex-1">
                      <span className="font-medium">{c.label}</span>
                      {c.clinical_date && (
                        <span className="text-xs text-slate-500 ml-2">
                          {dayjs(c.clinical_date).format("DD MMM YYYY")}
                        </span>
                      )}
                      {c.source_excerpt && (
                        <span className="block text-xs text-slate-600 italic mt-0.5">
                          « {c.source_excerpt} »
                        </span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
